import { User } from 'public-contracts';
import { IAuthClient, generateAvatar } from 'neko-common';

// Custom types to replace React Admin types
export interface UserIdentity {
  id: string;
  fullName: string;
  avatar?: string;
  roles?: string[];
}

export interface AuthProvider {
  login: (params: any) => Promise<any>;
  logout: (params?: any) => Promise<void | string | false>;
  checkAuth: (params?: any) => Promise<void>;
  checkError: (error: any) => Promise<void>;
  getPermissions: (params?: any) => Promise<any>;
  getIdentity: (params?: any) => Promise<UserIdentity>;
  forgotPassword?: (email: string) => Promise<any>;
  setNewPassword?: (params: { password: string }) => Promise<any>;
}
// Removed history import - using window.location for redirects
import { configs } from './configs';
import sdk from './sdks/master-data-api';
import tokenExpirationGraceService from './services/tokenExpirationGraceService';
import statePreservationService from './services/statePreservationService';
import inputActivityTracker from './services/inputActivityTracker';

// Debounced redirect handler that respects active input
let redirectTimeout: NodeJS.Timeout | null = null;
const REDIRECT_DELAY = 1500; // Wait 1.5 seconds after input stops before redirecting

// Ensure input activity tracker is initialized early
// The tracker starts automatically when imported, but we ensure it's ready
if (typeof window !== 'undefined') {
  // Force initialization check
  inputActivityTracker.isUserActivelyInputting();
}

const handleAuthenticatedRedirect = () => {
  // Don't redirect if this is just a token refresh (not during a login flow)
  // Only redirect during actual login flows (from callback or login page)
  // Check the current URL path to determine if we're in a login flow
  const currentPath = window.location.pathname;
  const isLoginFlow = currentPath === '/login' || 
                      currentPath === '/oauth/callback' || 
                      currentPath.startsWith('/oauth/callback');
  
  // If we're not in a login flow, this is likely a token refresh - don't redirect
  if (!isLoginFlow) {
    console.log('[AuthProvider] Token refresh detected (not in login flow), skipping redirect');
    return;
  }

  // Clear any pending redirect
  if (redirectTimeout) {
    clearTimeout(redirectTimeout);
    redirectTimeout = null;
  }

  // Check if user is actively inputting
  if (inputActivityTracker.isUserActivelyInputting()) {
    console.log(
      '[AuthProvider] User is actively inputting, deferring redirect...'
    );

    // Schedule redirect after user stops typing
    redirectTimeout = setTimeout(() => {
      // Double-check before redirecting
      if (!inputActivityTracker.isUserActivelyInputting()) {
        console.log(
          '[AuthProvider] User stopped inputting, preserving form data before redirect'
        );

        // Capture and save form data before redirecting
        const formData = inputActivityTracker.captureFormData();
        if (Object.keys(formData).length > 0) {
          console.log(
            '[AuthProvider] Captured form data before redirect:',
            Object.keys(formData).length,
            'fields'
          );
          statePreservationService.saveState({
            formData,
            preservedAt: Date.now(),
            reason: 'token_refresh_redirect',
          });
        }

        window.location.href = '/';
      } else {
        // Still active, reschedule
        handleAuthenticatedRedirect();
      }
    }, REDIRECT_DELAY);
  } else {
    // User is not actively inputting, but check for unsaved form data
    const hasUnsavedData = inputActivityTracker.hasUnsavedFormData();

    if (hasUnsavedData) {
      console.log(
        '[AuthProvider] Unsaved form data detected, preserving before redirect'
      );
      const formData = inputActivityTracker.captureFormData();
      if (Object.keys(formData).length > 0) {
        statePreservationService.saveState({
          formData,
          preservedAt: Date.now(),
          reason: 'token_refresh_redirect',
        });
      }
    }

    console.log(
      '[AuthProvider] User not actively inputting, redirecting immediately'
    );
    window.location.href = '/';
  }
};

const authProvider: AuthProvider & { auth: IAuthClient<User> } = {
  auth: sdk.createAuthClient(configs, handleAuthenticatedRedirect),
  login: async (authData: {
    username?: string;
    email?: string;
    password?: string;
    passkey?: boolean;
  }) => {
    const auth: IAuthClient<User> = authProvider.auth;
    if (await auth.isAuthenticated()) {
      // If already authenticated, restore any preserved state
      statePreservationService.restoreState();
      return;
    }
    if (!auth.isCallback()) {
      if (authData.passkey) {
        await auth.loginWithPasskey();
        return await auth.redirectToAuthServer();
      }

      // Validate credentials before proceeding
      if ((!authData.username && !authData.email) || !authData.password) {
        throw new Error('Username/email and password are required');
      }

      // Helper function to clean form input values
      // Strips trailing whitespace, newlines, and any trailing URLs/links
      const cleanFormValue = (value: string | undefined): string => {
        if (!value) return '';

        // Convert to string and remove newlines and carriage returns
        let cleaned = value.toString().replace(/[\r\n]+/g, '');

        // Remove trailing URLs/links (common when pasting from messages)
        // Matches URLs at the end of the string
        cleaned = cleaned.replace(/\s*(https?:\/\/[^\s]+)\s*$/gi, '');

        // Trim leading and trailing whitespace
        cleaned = cleaned.trim();

        return cleaned;
      };

      // Clean username/email and password
      if (authData.username) {
        authData.username = cleanFormValue(authData.username);
      }
      if (authData.email) {
        authData.email = cleanFormValue(authData.email);
      }
      if (authData.password) {
        authData.password = cleanFormValue(authData.password);
      }

      await auth.login({
        username: authData.username,
        email: authData.email,
        password: authData.password,
      });
      return await auth.redirectToAuthServer();
    }
  },
  logout: async () => {
    const auth: IAuthClient<User> = authProvider.auth;
    try {
      // Always attempt to logout regardless of current state
      await auth.logout();

      // Clear grace period and preserved state
      tokenExpirationGraceService.clear();
      statePreservationService.clearState();

      // Clear any client-side state or caches
      localStorage.clear();
      sessionStorage.clear();

      // Redirect to login page
      window.location.href = '/login';
    } catch (error) {
      console.error('Error during logout:', error);
      // Still redirect to login even if logout fails
      window.location.href = '/login';
    }
  },
  checkError: async (error) => {
    if (error.status === 401) {
      const auth: IAuthClient<User> = authProvider.auth;

      // Check if we're in grace period - if so, allow the request to proceed
      // The actual token refresh will happen when user tries to perform an action
      if (tokenExpirationGraceService.isInGracePeriod()) {
        console.log(
          '401 error detected but in grace period, allowing to proceed'
        );
        // Try to refresh token in background without blocking
        auth.fetchRefreshToken().catch((err) => {
          console.error('Background token refresh failed:', err);
        });
        return; // Allow request to proceed during grace period
      }

      // First, try to refresh the token before logging out
      try {
        console.log('401 error detected, attempting to refresh token...');
        await auth.fetchRefreshToken();
        console.log('Token refresh successful, retrying original request');

        // Restore state after successful refresh
        statePreservationService.restoreState();

        return; // Return to allow React Admin to retry the original request
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);

        // Save state before redirecting
        statePreservationService.saveState();

        // Only logout if refresh fails and not in grace period
        await auth.logout();

        // Check if user is actively inputting before redirecting
        if (inputActivityTracker.isUserActivelyInputting()) {
          console.log(
            '401 error: User is actively inputting, deferring redirect to login'
          );
          setTimeout(() => {
            if (!inputActivityTracker.isUserActivelyInputting()) {
              console.log(
                '401 error: User stopped inputting, preserving form data before redirect'
              );

              // Capture and save form data before redirecting
              const formData = inputActivityTracker.captureFormData();
              if (Object.keys(formData).length > 0) {
                statePreservationService.saveState({
                  formData,
                  preservedAt: Date.now(),
                  reason: 'token_refresh_failed_redirect',
                });
              }

              window.location.href = '/login';
            }
          }, REDIRECT_DELAY);
        } else {
          // Check for unsaved form data
          const hasUnsavedData = inputActivityTracker.hasUnsavedFormData();
          if (hasUnsavedData) {
            console.log(
              '401 error: Unsaved form data detected, preserving before redirect'
            );
            const formData = inputActivityTracker.captureFormData();
            if (Object.keys(formData).length > 0) {
              statePreservationService.saveState({
                formData,
                preservedAt: Date.now(),
                reason: 'token_refresh_failed_redirect',
              });
            }
          }

          window.location.href = '/login';
        }
        return;
      }
    }
    if (error.status === 403) {
      window.location.href = '/forbidden';
      return;
    }
  },
  checkAuth: async () => {
    const auth: IAuthClient<User> = authProvider.auth;

    console.log('React Admin checkAuth called');

    // Handle callback first
    if (auth.isCallback()) {
      try {
        await auth.onCallback();
        // After successful callback, restore any preserved state
        setTimeout(() => {
          statePreservationService.restoreState();
        }, 500); // Small delay to ensure auth state is fully set

        // Send token to Chrome extension after successful login
        setTimeout(async () => {
          try {
            const token = await auth.getAccessToken();
            if (token) {
              window.postMessage(
                {
                  type: 'NEKO_EXTENSION_TOKEN_UPDATE',
                  token: token,
                },
                window.location.origin
              );
              console.log(
                '[POS Dashboard] Token sent to extension after login'
              );
            }
          } catch (tokenError) {
            console.warn(
              '[POS Dashboard] Could not send token to extension:',
              tokenError
            );
          }
        }, 1000);

        return; // Just return, let the app handle the redirect
      } catch (error) {
        console.error('Error during callback:', error);
        window.location.href = '/login';
        throw error;
      }
    }

    // Handle login page
    if (auth.isLogin()) {
      console.log('React Admin checkAuth: On login page, returning');
      return;
    }

    // Check current auth state
    const [authenticated, redirectedToAuthServer, tokenExpired] =
      await Promise.all([
        auth.isAuthenticated(),
        auth.isRedirectedToAuthServer(),
        auth.isTokenExpire(),
      ]);

    console.log('React Admin checkAuth state:', {
      authenticated,
      redirectedToAuthServer,
      tokenExpired,
      currentPath: window.location.pathname,
    });

    // Allow the auth flow to proceed during transitions
    if (redirectedToAuthServer || auth.isCallback()) {
      console.log('React Admin checkAuth: In transition state, returning');
      return;
    }

    // Check authentication state
    if (!authenticated) {
      console.log(
        'React Admin checkAuth: Not authenticated, checking if we should redirect'
      );
      // Only redirect if we're not in a transition state and user is not actively inputting
      if (!auth.isLogin() && !redirectedToAuthServer && !auth.isCallback()) {
        // Check if user is actively inputting - if so, defer redirect
        if (inputActivityTracker.isUserActivelyInputting()) {
          console.log(
            'React Admin checkAuth: User is actively inputting, deferring redirect to login'
          );
          // Schedule redirect after user stops typing
          setTimeout(() => {
            if (!inputActivityTracker.isUserActivelyInputting()) {
              console.log(
                'React Admin checkAuth: User stopped inputting, preserving form data before redirect'
              );

              // Capture and save form data before redirecting
              const formData = inputActivityTracker.captureFormData();
              if (Object.keys(formData).length > 0) {
                statePreservationService.saveState({
                  formData,
                  preservedAt: Date.now(),
                  reason: 'auth_required_redirect',
                });
              }

              window.location.href = '/login';
            }
          }, REDIRECT_DELAY);
        } else {
          // Check for unsaved form data even if not actively typing
          const hasUnsavedData = inputActivityTracker.hasUnsavedFormData();
          if (hasUnsavedData) {
            console.log(
              'React Admin checkAuth: Unsaved form data detected, preserving before redirect'
            );
            const formData = inputActivityTracker.captureFormData();
            if (Object.keys(formData).length > 0) {
              statePreservationService.saveState({
                formData,
                preservedAt: Date.now(),
                reason: 'auth_required_redirect',
              });
            }
          }

          console.log('React Admin checkAuth: Redirecting to login');
          window.location.href = '/login';
        }
      }
      throw new Error('pos.auth.not_authenticated');
    }

    console.log('React Admin checkAuth: Authenticated successfully');

    // Handle token refresh if needed
    if (tokenExpired) {
      // Check if we're in grace period
      if (tokenExpirationGraceService.isInGracePeriod()) {
        console.log(
          'React Admin checkAuth: Token expired but in grace period, allowing to continue'
        );
        // Try to refresh token in background without blocking
        auth
          .fetchRefreshToken()
          .then(() => {
            console.log('Background token refresh successful');
            tokenExpirationGraceService.endGracePeriod();
            // Restore state after successful refresh
            statePreservationService.restoreState();
          })
          .catch((err: unknown) => {
            console.error('Background token refresh failed:', err);
          });
        return; // Allow to continue during grace period
      }

      // Start grace period instead of immediately redirecting
      // Use current time as expiration time since we detected expiration
      const expirationTime = Date.now();
      tokenExpirationGraceService.startGracePeriod(expirationTime);
      console.log(
        'React Admin checkAuth: Token expired, starting grace period'
      );

      // Try to refresh token in background
      auth
        .fetchRefreshToken()
        .then(() => {
          console.log('Background token refresh successful');
          tokenExpirationGraceService.endGracePeriod();
          // Restore state after successful refresh
          statePreservationService.restoreState();
        })
        .catch((error) => {
          console.error('Background token refresh failed:', error);
          // If refresh fails, we'll handle it when user tries to perform an action
        });

      return; // Allow to continue during grace period
    }
  },
  getPermissions: () => Promise.resolve(),
  getIdentity: async () => {
    const auth: IAuthClient<User> = authProvider.auth;

    console.log('React Admin getIdentity called');

    // Don't try to get identity if we're on login page - check URL directly
    if (
      window.location.pathname === '/login' ||
      window.location.pathname.includes('/login') ||
      window.location.pathname === '/oauth/callback'
    ) {
      console.log(
        'React Admin getIdentity: On login/callback page, throwing error'
      );
      throw new Error('pos.auth.not_authenticated');
    }

    // Don't try to get identity if we're in auth transitions
    console.log('React Admin getIdentity: Checking auth state transitions...');
    let isLogin, isRedirected, isCallback;
    try {
      [isLogin, isRedirected, isCallback] = await Promise.all([
        auth.isLogin(),
        auth.isRedirectedToAuthServer(),
        auth.isCallback(),
      ]);
      console.log(
        'React Admin getIdentity: Auth state checks completed successfully'
      );
    } catch (error) {
      console.error(
        'React Admin getIdentity: Error checking auth state:',
        error
      );
      // If auth state checks fail, assume we're not in a transition state
      isLogin = false;
      isRedirected = false;
      isCallback = false;
    }

    console.log('React Admin getIdentity state:', {
      isLogin,
      isRedirected,
      isCallback,
      currentPath: window.location.pathname,
    });

    if (isLogin || isRedirected || isCallback) {
      console.log(
        'React Admin getIdentity: In transition state, throwing error'
      );
      throw new Error('pos.auth.not_authenticated');
    }

    console.log(
      'React Admin getIdentity: Past transition state checks, checking authentication...'
    );

    // TEMPORARY WORKAROUND: If we're on a React Admin page and authenticated,
    // return a mock identity to prevent infinite loops
    let authenticated;
    try {
      authenticated = await auth.isAuthenticated();
      console.log(
        'React Admin getIdentity: Authentication check result:',
        authenticated
      );
    } catch (error) {
      console.error(
        'React Admin getIdentity: Error checking authentication:',
        error
      );
      authenticated = false;
    }

    // Remove the mock identity logic since we're no longer using React Admin
    // This was causing the navigation to show nothing because it returned empty roles

    try {
      // Check if authenticated
      const authenticated = await auth.isAuthenticated();
      console.log(
        'React Admin getIdentity: Authentication check result:',
        authenticated
      );

      if (!authenticated) {
        console.log(
          'React Admin getIdentity: Not authenticated, throwing error'
        );
        throw new Error('pos.auth.not_authenticated');
      }

      // Try to get profile once, with better error handling
      let profile;
      try {
        console.log('React Admin getIdentity: Fetching user profile...');
        profile = await auth.getUserProfile();
        console.log(
          'React Admin getIdentity: User profile fetched successfully:',
          profile
        );
      } catch (error: unknown) {
        console.error(
          'React Admin getIdentity: Failed to fetch user profile:',
          error
        );
        // If it's a network error (API server not running), don't retry
        if (error instanceof Error && error.message === 'Failed to fetch') {
          throw new Error('API server not available');
        }
        throw error;
      }

      if (!profile) {
        console.log(
          'React Admin getIdentity: Profile is null/undefined, throwing error'
        );
        throw new Error('Failed to load user profile after multiple attempts');
      }

      const fullName = profile.firstName + ' ' + profile.lastName;
      const userIdentity: UserIdentity = {
        id: profile.id,
        fullName,
        avatar: profile.avatar ?? generateAvatar(fullName),
        roles: profile.roles || [],
      };

      console.log(
        'React Admin getIdentity: Returning user identity:',
        userIdentity
      );
      return userIdentity;
    } catch (error: unknown) {
      console.error('Error getting identity:', error);
      // Remove the fallback identity logic since we're no longer using React Admin
      // This was also causing navigation issues with empty roles
      // Don't redirect from getIdentity - let React Admin handle it
      throw error;
    }
  },
  forgotPassword: async (email: string) => {
    const auth: IAuthClient<User> = authProvider.auth;
    return await auth.forgotPassword(email);
  },
  setNewPassword: async ({ password }: { password: string }) => {
    const auth: IAuthClient<User> = authProvider.auth;
    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) {
      throw new Error('Invalid token');
    }
    return await auth.setNewPassword(password, token);
  },
};

export default authProvider;
